// disOS profile: two tabs — Account info (who's signed in, sign out) and Settings (per-user report defaults).
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/authStore';
import { FirmDefaultsForm } from '../dis/FirmDefaultsForm';
import { usePubBody } from './pubBody';
import './public.css';

export function Profile() {
  usePubBody();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const user = useAuth((s) => s.user);
  const firm = useAuth((s) => s.firm);
  const logout = useAuth((s) => s.logout);
  const saveFirm = useAuth((s) => s.saveFirm);
  const updateEmail = useAuth((s) => s.updateEmail);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'account' | 'settings'>(params.get('tab') === 'settings' ? 'settings' : 'account');

  const [accName, setAccName] = useState(firm.name ?? '');
  const [accEmail, setAccEmail] = useState(user?.email ?? '');
  const [accState, setAccState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [accErr, setAccErr] = useState('');
  const accDirty = accName !== (firm.name ?? '') || accEmail.trim().toLowerCase() !== (user?.email ?? '');

  async function saveAccount() {
    setAccState('saving'); setAccErr('');
    try {
      if (accName !== (firm.name ?? '')) await saveFirm({ ...firm, name: accName.trim() });
      const e = accEmail.trim().toLowerCase();
      if (e && e !== (user?.email ?? '')) await updateEmail(e);
      setAccState('saved');
    } catch (err) {
      setAccErr(err instanceof Error ? err.message : String(err));
      setAccState('idle');
    }
  }

  async function signOut() {
    setBusy(true);
    await logout();
    nav('/', { replace: true });
  }

  const initial = (user?.email ?? '?').charAt(0).toUpperCase();
  return (
    <div className="pub">
      <div className="profwrap">
        <Link className="back" to="/app">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
          Back to workspace
        </Link>
        <h1>Your account</h1>

        <div className="proftabs" role="tablist">
          <button type="button" role="tab" className={tab === 'account' ? 'on' : ''} aria-selected={tab === 'account'} onClick={() => setTab('account')}>Account info</button>
          <button type="button" role="tab" className={tab === 'settings' ? 'on' : ''} aria-selected={tab === 'settings'} onClick={() => setTab('settings')}>Settings</button>
        </div>

        {tab === 'account' ? (
          <>
            <div className="profrow">
              <div className="avatar">{initial}</div>
              <div className="fds" style={{ flex: 1 }}>
                <label className="fds-field"><span>Name</span>
                  <input value={accName} onChange={(e) => { setAccName(e.target.value); setAccState('idle'); setAccErr(''); }} placeholder="Your name or firm name" /></label>
                <label className="fds-field"><span>Email</span>
                  <input type="email" value={accEmail} onChange={(e) => { setAccEmail(e.target.value); setAccState('idle'); setAccErr(''); }} placeholder="you@example.com" /></label>
                {accErr && <div className="val" style={{ color: 'var(--red)', fontSize: 12.5 }}>{accErr}</div>}
                <div className="fds-actions">
                  <button className="btn" type="button" onClick={saveAccount} disabled={accState === 'saving' || !accDirty}>
                    {accState === 'saving' ? 'Saving…' : accState === 'saved' && !accDirty ? 'Saved ✓' : 'Save account'}
                  </button>
                </div>
              </div>
            </div>
            <div className="profrow actions">
              <div className="who">
                <div className="lbl">Session</div>
                <div className="val">Sign out of this browser</div>
              </div>
              <button className="btn danger" type="button" onClick={signOut} disabled={busy}>{busy ? 'Signing out…' : 'Sign out'}</button>
            </div>
          </>
        ) : (
          <FirmDefaultsForm />
        )}
      </div>
    </div>
  );
}
